import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 * @property {number} activeCategoryIndex - The active category index in three-panel mode.
 * @property {string | null} defaultPromoImage - The default promo image URL for current parent.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  /**
   * Promo configuration from JSON script tag
   * @type {Object<string, {image: string | null, link: string, text: string}>}
   */
  #promoConfig = {};

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    onDocumentLoaded(this.#preloadImages);

    // Initialize three-panel mega menu if enabled
    if (this.dataset.menuStyle === 'three_panel') {
      this.#initThreePanelMenu();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Initialize three-panel mega menu functionality
   */
  #initThreePanelMenu() {
    const promoScript = this.querySelector('[data-mega-menu-promos]');
    if (promoScript) {
      try {
        this.#promoConfig = JSON.parse(promoScript.textContent || '{}');
      } catch {
        this.#promoConfig = {};
      }
    }

    const panels = this.querySelectorAll('.mega-menu-panel');
    panels.forEach((panel) => this.#setupPanelListeners(panel));
  }

  /**
   * Set up event listeners for a three-panel mega menu
   * @param {Element} panel - The mega menu panel element
   */
  #setupPanelListeners(panel) {
    const categoryItems = panel.querySelectorAll('.mega-menu-panel__category-item');

    categoryItems.forEach((item) => {
      item.addEventListener(
        'pointerenter',
        () => {
          this.#handleCategoryHover(panel, item);
        },
        { signal: this.#abortController.signal },
      );
    });
  }

  /**
   * Handle category item hover in three-panel menu
   * @param {Element} panel - The mega menu panel
   * @param {Element} categoryItem - The hovered category item
   */
  #handleCategoryHover(panel, categoryItem) {
    if (!(categoryItem instanceof HTMLElement)) return;

    const categoryIndex = categoryItem.dataset.categoryIndex;
    const hasChildren = categoryItem.dataset.hasChildren === 'true';
    const promoVideo = categoryItem.dataset.promoVideo;
    const promoImage = categoryItem.dataset.promoImage;
    const promoImageAlt = categoryItem.dataset.promoImageAlt;

    // Always update active category styling in left panel
    const allCategories = panel.querySelectorAll('.mega-menu-panel__category-item');
    allCategories.forEach((cat) => cat.classList.remove('mega-menu-panel__category-item--active'));
    categoryItem.classList.add('mega-menu-panel__category-item--active');

    if (hasChildren) {
      const allSubcategories = panel.querySelectorAll('.mega-menu-panel__subcategories');
      allSubcategories.forEach((sub) => sub.classList.remove('mega-menu-panel__subcategories--active'));

      const activeSubcategories = panel.querySelector(`[data-subcategories-index="${categoryIndex}"]`);
      if (activeSubcategories) {
        activeSubcategories.classList.add('mega-menu-panel__subcategories--active');
      }

      const allShowAllWrappers = panel.querySelectorAll('.mega-menu-panel__show-all-wrapper');
      allShowAllWrappers.forEach((wrapper) => wrapper.classList.remove('mega-menu-panel__show-all-wrapper--active'));

      const activeShowAll = panel.querySelector(`[data-show-all-index="${categoryIndex}"]`);
      if (activeShowAll) {
        activeShowAll.classList.add('mega-menu-panel__show-all-wrapper--active');
      }

      const promoContainer = panel.querySelector('[data-panel-promo]');

      if (promoContainer) {
        const defaultMedia = promoContainer.querySelector('[data-default-promo-media]');
        const defaultMediaUrl = this.#getDefaultMediaUrl(defaultMedia);

        if (promoVideo) {
          const defaultFilename = defaultMediaUrl?.split('?')[0]?.split('/').pop() || '';
          if (defaultFilename && promoVideo.includes(defaultFilename)) {
            this.#resetPromoMedia(promoContainer);
          } else {
            this.#updatePromoMedia(promoContainer, promoVideo, 'video');
          }
        } else if (promoImage) {
          if (defaultMediaUrl && promoImage === defaultMediaUrl) {
            this.#resetPromoMedia(promoContainer);
          } else {
            this.#updatePromoMedia(promoContainer, promoImage, 'image', promoImageAlt);
          }
        } else {
          this.#resetPromoMedia(promoContainer);
        }
      }
    }
  }

  /**
   * Update the promo media in the right panel
   * @param {Element} promoContainer - The promo container element
   * @param {string} mediaUrl - The new media URL
   * @param {'video'|'image'} mediaType - The type of media
   * @param {string} [altText] - The alt text for images
   */
  #updatePromoMedia(promoContainer, mediaUrl, mediaType, altText) {
    const hoverImage = promoContainer.querySelector('[data-hover-promo-image]');
    const hoverVideo = promoContainer.querySelector('[data-hover-promo-video]');
    const defaultMedia = promoContainer.querySelector('[data-default-promo-media]');

    if (defaultMedia) {
      /** @type {HTMLElement} */ (defaultMedia).style.display = 'none';
    }

    if (mediaType === 'video') {
      if (hoverImage) {
        /** @type {HTMLElement} */ (hoverImage).style.display = 'none';
      }
      if (hoverVideo) {
        /** @type {HTMLVideoElement} */ (hoverVideo).src = mediaUrl;
        /** @type {HTMLElement} */ (hoverVideo).style.display = 'block';
        /** @type {HTMLVideoElement} */ (hoverVideo).play();
      }
    } else {
      if (hoverVideo) {
        /** @type {HTMLElement} */ (hoverVideo).style.display = 'none';
      }
      if (hoverImage) {
        /** @type {HTMLImageElement} */ (hoverImage).src = mediaUrl;
        if (altText) {
          /** @type {HTMLImageElement} */ (hoverImage).alt = altText;
        }
        /** @type {HTMLElement} */ (hoverImage).style.display = 'block';
      }
    }
  }

  /**
   * Reset the promo media to default
   * @param {Element} promoContainer - The promo container element
   */
  #resetPromoMedia(promoContainer) {
    const hoverImage = promoContainer.querySelector('[data-hover-promo-image]');
    const hoverVideo = promoContainer.querySelector('[data-hover-promo-video]');
    const defaultMedia = promoContainer.querySelector('[data-default-promo-media]');

    if (hoverImage) {
      /** @type {HTMLElement} */ (hoverImage).style.display = 'none';
    }
    if (hoverVideo) {
      /** @type {HTMLElement} */ (hoverVideo).style.display = 'none';
      /** @type {HTMLVideoElement} */ (hoverVideo).pause?.();
    }

    if (defaultMedia) {
      /** @type {HTMLElement} */ (defaultMedia).style.display = '';
    }
  }

  /**
   * Get the URL of the default media element
   * @param {Element|null} defaultMedia - The default media element
   * @returns {string|null} The media URL or null
   */
  #getDefaultMediaUrl(defaultMedia) {
    if (!defaultMedia) return null;

    const video = defaultMedia.querySelector('video');
    if (video) {
      const source = video.querySelector('source');
      return source?.src || video.src || null;
    }

    if (defaultMedia instanceof HTMLImageElement) {
      return defaultMedia.src || null;
    }

    return null;
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
    activeCategoryIndex: 0,
    defaultPromoImage: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    let overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    const submenuHeight = submenu ? Math.max(submenu.offsetHeight, overflowMenuHeight) : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');

    if (submenu) {
      document.documentElement.setAttribute('scroll-lock', '');
    }
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    document.documentElement.removeAttribute('scroll-lock');

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
